import { Edge, MarkerType, Node } from 'reactflow';
import { fetchItems } from '../../../../support/queries';
import { JourneyNodeData, ProviderOption } from './journeyMapCanvasTypes';
import {
  JourneyMapDraftLink,
  JourneyMapEdgeDashStyle,
  JourneyMapEdgeShape,
} from '../../../../types/journeyMap';
import {
  resolveSourceId,
  resolveTargetId,
} from '../../../../support/journeyMap/normalizers';
import {
  getContentOptionLabel,
  getContentRefTypeOption,
  getProviderOption,
} from '../../../../support/journeyMap/contentSources';

const HARBOR = {
  neutral: {
    weak: '#889099',
    borderWeak: '#E1E4E8',
  },
  info: {
    default: '#2774D9',
    bgWeak: '#EAF2FE',
  },
  interact: {
    default: '#1D69CC',
    bgWeak: '#EAF3FF',
  },
  success: {
    default: '#45991F',
    bgWeak: '#EAF6E3',
  },
  warning: {
    default: '#BD7202',
    bgWeak: '#FFF3E0',
  },
  danger: {
    default: '#CC2D37',
    bgWeak: '#FDEBEC',
  },
};

export type JourneyMapHandleId =
  | 'source-top'
  | 'source-right'
  | 'source-bottom'
  | 'source-left'
  | 'target-top'
  | 'target-right'
  | 'target-bottom'
  | 'target-left';

export function getContentDisplayName(item: any) {
  if (!item) return '';
  return getContentOptionLabel(item, ['title', 'name', 'id']);
}

export function getLinkTypeAppearance(linkType: string, selected = false) {
  const selectedBoost = selected ? 1.3 : 1;

  switch (linkType) {
    case 'alternate':
      return {
        label: 'Alternate',
        color: HARBOR.info.default,
        bg: HARBOR.info.bgWeak,
        type: 'smoothstep' as const,
        animated: false,
        dashArray: undefined,
        strokeWidth: 2.5 * selectedBoost,
      };
    case 'conditional':
      return {
        label: 'Conditional',
        color: HARBOR.interact.default,
        bg: HARBOR.interact.bgWeak,
        type: 'smoothstep' as const,
        animated: true,
        dashArray: '6 4',
        strokeWidth: 2.5 * selectedBoost,
      };
    case 'test_out':
      return {
        label: 'Test-Out',
        color: HARBOR.warning.default,
        bg: HARBOR.warning.bgWeak,
        type: 'smoothstep' as const,
        animated: true,
        dashArray: '10 4',
        strokeWidth: 3 * selectedBoost,
      };
    case 'optional':
      return {
        label: 'Optional',
        color: HARBOR.neutral.weak,
        bg: '#F7F7F7',
        type: 'smoothstep' as const,
        animated: false,
        dashArray: '3 5',
        strokeWidth: 2 * selectedBoost,
      };
    case 'fallback':
      return {
        label: 'Fallback',
        color: HARBOR.danger.default,
        bg: HARBOR.danger.bgWeak,
        type: 'smoothstep' as const,
        animated: false,
        dashArray: '12 6',
        strokeWidth: 2.5 * selectedBoost,
      };
    case 'pass':
      return {
        label: 'Pass',
        color: HARBOR.success.default,
        bg: HARBOR.success.bgWeak,
        type: 'smoothstep' as const,
        animated: false,
        dashArray: undefined,
        strokeWidth: 2.25 * selectedBoost,
      };
    case 'fail':
      return {
        label: 'Fail',
        color: HARBOR.danger.default,
        bg: HARBOR.danger.bgWeak,
        type: 'smoothstep' as const,
        animated: false,
        dashArray: undefined,
        strokeWidth: 2.25 * selectedBoost,
      };
    case 'skip':
      return {
        label: 'Skip',
        color: '#6F7680',
        bg: '#F7F7F7',
        type: 'smoothstep' as const,
        animated: false,
        dashArray: '4 4',
        strokeWidth: 2 * selectedBoost,
      };
    case 'default':
    default:
      return {
        label: 'Default',
        color: '#656C75',
        bg: '#F7F7F7',
        type: 'smoothstep' as const,
        animated: false,
        dashArray: undefined,
        strokeWidth: 2 * selectedBoost,
      };
  }
}

export function getLinkSummary(link: any) {
  const config = link.condition_config || {};

  if (link.label?.trim()) return link.label.trim();

  if (link.link_type === 'test_out') {
    const basis = config.rule_basis;
    const operator = config.operator;
    const value = config.value;

    if (basis === 'score' && value !== undefined && value !== null) {
      return `Test-Out: Score ${operator || '>='} ${value}`;
    }
    if (basis === 'attempts' && value !== undefined && value !== null) {
      return `Test-Out: Attempts ${operator || '<='} ${value}`;
    }
    if (basis === 'completion_status' && value) {
      return `Test-Out: Status = ${value}`;
    }
    if (basis === 'provider_result' && config.provider_key) {
      return `Test-Out: ${config.provider_key} = ${value ?? ''}`;
    }
    return 'Test-Out';
  }

  if (link.link_type === 'conditional') {
    if (link.condition_type === 'status_equals' && config.status) {
      return `Status = ${config.status}`;
    }
    if (link.condition_type === 'rule' && config.rule_expression) {
      return String(config.rule_expression);
    }
    return 'Conditional';
  }

  return getLinkTypeAppearance(link.link_type || 'default').label;
}

export async function loadReferenceLabelMap(
  draft: any,
  providerOptions: ProviderOption[],
): Promise<Record<string, string>> {
  if (!draft?.items?.length) return {};

  const nextMap: Record<string, string> = {};

  const requestBuckets = new Map<
    string,
    {
      endpoint: string;
      displayFields: string[];
      contentRefType: string;
      contentRefIds: Set<string>;
    }
  >();

  draft.items
    .filter((item: any) => !item.isDeleted && item.content_ref_id && item.content_ref_type)
    .forEach((item: any) => {
      const contentRefType = String(item.content_ref_type || '').trim();
      const contentRefTypeOption = getContentRefTypeOption(contentRefType);
      if (!contentRefTypeOption?.endpoint) return;

      const endpoint = contentRefTypeOption.endpoint.endsWith('/')
        ? contentRefTypeOption.endpoint
        : `${contentRefTypeOption.endpoint}/`;

      const displayFields = contentRefTypeOption.displayFields || ['title', 'name', 'id'];
      const bucketKey = `${contentRefType}::${endpoint}::${displayFields.join('|')}`;

      const existing = requestBuckets.get(bucketKey);
      if (existing) {
        existing.contentRefIds.add(String(item.content_ref_id));
        return;
      }

      requestBuckets.set(bucketKey, {
        endpoint,
        displayFields,
        contentRefType,
        contentRefIds: new Set([String(item.content_ref_id)]),
      });
    });

  for (const [, bucket] of requestBuckets.entries()) {
    try {
      const rows = await fetchItems(bucket.endpoint);

      (Array.isArray(rows) ? rows : []).forEach((row: any) => {
        const rowId = String(row?.id ?? '');
        if (!rowId || !bucket.contentRefIds.has(rowId)) return;

        // FIX: key by content_ref_type + id so identical numeric IDs across sources won't collide
        const compositeKey = `${bucket.contentRefType}::${rowId}`;
        nextMap[compositeKey] = getContentOptionLabel(row, bucket.displayFields);
      });
    } catch {
      // ignore and allow fallback rendering
    }
  }

  return nextMap;
}

export function buildCanvasNodes(
  draft: any,
  visibleItems: any[],
  selectedItemTempId: string | null,
  referenceLabelMap: Record<string, string>,
  canvasWidth: number,
  canvasHeight: number,
): Node[] {
  if (!draft || !visibleItems.length) {
    return [
      {
        id: 'start-placeholder',
        type: 'startPlaceholder',
        position: { x: canvasWidth * 0.2, y: canvasHeight * 0.2 },
        data: {},
        draggable: false,
        selectable: false,
      },
    ];
  }

  return visibleItems.map((item) => {
    const providerOption = getProviderOption(item.provider || '');
    const providerLabel =
      providerOption?.label ||
      String(item.provider || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase());

    const compositeRefKey = `${String(item.content_ref_type || '').trim()}::${String(
      item.content_ref_id || ''
    ).trim()}`;

    const subtitle =
      referenceLabelMap[compositeRefKey] ||
      String(item?.metadata?.resolvedReferencedTitle || '').trim() ||
      String(item?.metadata?.dcloud_selected_item?.name || '').trim() ||
      '';

    const attachedFile = item?.provider_config?.file || null;

    return {
      id: item.tempId,
      type: 'journeyItem',
      position: {
        x: item.position_x ?? 0,
        y: item.position_y ?? 0,
      },
      data: {
        title: item.name || 'Untitled',
        provider: providerLabel,
        referenceLabel: subtitle,
        itemTempId: item.tempId,
        itemType: item.item_type || 'content',
        selected: selectedItemTempId === item.tempId,
        displayLabel: String(item?.metadata?.label || '').trim() || undefined,
        fileRenderKind: attachedFile ? String(attachedFile.render_kind || '') : undefined,
        fileName: attachedFile ? String(attachedFile.file_name || '') : undefined,
      } as JourneyNodeData,
    };
  });
}

export function getNearestHandleId(params: {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  handleType: 'source' | 'target';
}): JourneyMapHandleId {
  const { fromX, fromY, toX, toY, handleType } = params;

  const dx = toX - fromX;
  const dy = toY - fromY;

  if (Math.abs(dx) > Math.abs(dy)) {
    return dx >= 0
      ? (`${handleType}-right` as JourneyMapHandleId)
      : (`${handleType}-left` as JourneyMapHandleId);
  }

  return dy >= 0
    ? (`${handleType}-bottom` as JourneyMapHandleId)
    : (`${handleType}-top` as JourneyMapHandleId);
}

export function resolveNearestHandlesForItems({
                                                sourceItem,
                                                targetItem,
                                              }: {
  sourceItem: any;
  targetItem: any;
}) {
  const sourceX = Number(sourceItem?.position_x || 0);
  const sourceY = Number(sourceItem?.position_y || 0);
  const targetX = Number(targetItem?.position_x || 0);
  const targetY = Number(targetItem?.position_y || 0);

  return {
    sourceHandle: getNearestHandleId({
      fromX: sourceX,
      fromY: sourceY,
      toX: targetX,
      toY: targetY,
      handleType: 'source',
    }),
    targetHandle: getNearestHandleId({
      fromX: targetX,
      fromY: targetY,
      toX: sourceX,
      toY: sourceY,
      handleType: 'target',
    }),
  };
}

export function resolveEdgeType(
  link: JourneyMapDraftLink,
  fallbackType: 'smoothstep' | 'default' | 'step' | 'straight',
) {
  const shape = String(link?.edge_shape || '').toLowerCase() as JourneyMapEdgeShape | '';

  if (shape === 'straight') return 'straight';
  if (shape === 'bezier') return 'default';
  if (shape === 'step') return 'step';
  if (shape === 'smoothstep') return 'smoothstep';

  return fallbackType;
}

export function resolveEdgeDashArray(
  link: JourneyMapDraftLink,
  fallbackDashArray?: string,
) {
  const dashStyle = String(link?.edge_dash_style || '').toLowerCase() as JourneyMapEdgeDashStyle | '';

  if (dashStyle === 'solid') return undefined;
  if (dashStyle === 'dashed') return '8 6';
  if (dashStyle === 'dotted') return '2 6';

  return fallbackDashArray;
}

export function buildCanvasEdges(
  draft: any,
  visibleLinks: JourneyMapDraftLink[],
  selectedLinkTempId: string | null,
): Edge[] {
  if (!draft) return [];

  return visibleLinks
    .filter((link) => link.from_item_temp_id && link.to_item_temp_id)
    .map((link): Edge => {
      const isSelected = selectedLinkTempId === link.tempId;
      const appearance = getLinkTypeAppearance(link.link_type || 'default', isSelected);

      const edgeShape =
        link.edge_shape === 'straight'
          ? 'straight'
          : link.edge_shape === 'bezier'
            ? 'bezier'
            : link.edge_shape === 'step'
              ? 'step'
              : 'smoothstep';

      const strokeWidth =
        link.edge_thickness != null ? Number(link.edge_thickness) : appearance.strokeWidth;

      const dashArray = resolveEdgeDashArray(link, appearance.dashArray);

      const animated =
        typeof link.edge_animated === 'boolean' ? link.edge_animated : appearance.animated;

      return {
        id: link.tempId,
        source: String(link.from_item_temp_id),
        target: String(link.to_item_temp_id),
        label: getLinkSummary(link),
        type: 'journeyMapCustom',
        animated,
        style: {
          stroke: appearance.color,
          strokeWidth,
          strokeDasharray: dashArray,
          opacity: isSelected ? 1 : 0.95,
        },
        data: {
          edgeShape,
          stroke: appearance.color,
          strokeWidth,
          strokeDasharray: dashArray,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: appearance.color,
        },
      };
    });
}

export function resolveDelimiterColor(color?: string) {
  switch (color) {
    case 'interact':
      return '#1D69CC';
    case 'info':
      return '#78A9EA';
    case 'success':
      return '#73B857';
    case 'warning':
      return '#D89A36';
    case 'danger':
      return '#E27D84';
    case 'neutral':
    default:
      return '#889099';
  }
}

export function buildJourneyMapItemIdLookup(draft: any): Record<string, string> {
  const map: Record<string, string> = {};
  (draft?.items || []).forEach((item: any) => {
    if (item?.id != null) {
      map[String(item.id)] = item.tempId;
    }
  });
  return map;
}

export function normalizeDraftLinksWithTempIds(draft: any): JourneyMapDraftLink[] {
  const idLookup = buildJourneyMapItemIdLookup(draft);

  return (draft?.links || []).map((link: any) => {
    const fromTempId =
      link.from_item_temp_id ||
      (link.from_item != null ? idLookup[String(link.from_item)] || null : null) ||
      (resolveSourceId(link) ? idLookup[String(resolveSourceId(link))] || null : null);

    const toTempId =
      link.to_item_temp_id ||
      (link.to_item != null ? idLookup[String(link.to_item)] || null : null) ||
      (resolveTargetId(link) ? idLookup[String(resolveTargetId(link))] || null : null);

    return {
      ...link,
      from_item_temp_id: fromTempId,
      to_item_temp_id: toTempId,
      source_handle:
        link.source_handle || link?.condition_config?.source_handle || null,
      target_handle:
        link.target_handle || link?.condition_config?.target_handle || null,
      edge_shape:
        link.edge_shape || link?.condition_config?.edge_shape || 'smoothstep',
      edge_thickness:
        link.edge_thickness ?? link?.condition_config?.edge_thickness ?? null,
      edge_dash_style:
        link.edge_dash_style || link?.condition_config?.edge_dash_style || null,
      edge_animated:
        typeof link.edge_animated === 'boolean'
          ? link.edge_animated
          : typeof link?.condition_config?.edge_animated === 'boolean'
            ? link.condition_config.edge_animated
            : null,
    };
  });
}